import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { numbers } from "../../data/content";
import OrbitPattern from "../OrbitPattern";

const ease = [0.16, 1, 0.3, 1];

const Counter = ({ value, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  
  const parseValue = (val) => {
    const match = val.match(/^(\+?)(\d+)(K?)(\+?)$/i);
    if (!match) return { prefix: "", num: 0, suffixK: "", suffixPlus: "", raw: val };
    return {
      prefix: match[1] || "",
      num: parseInt(match[2], 10),
      suffixK: match[3] || "",
      suffixPlus: match[4] || "",
      raw: null
    };
  };

  const parsed = parseValue(value);
  const [display, setDisplay] = useState(() => {
    if (parsed.raw !== null) return parsed.raw;
    return `${parsed.prefix}0${parsed.suffixK}${parsed.suffixPlus}`;
  });

  useEffect(() => {
    if (!inView) return;
    if (parsed.raw !== null) { setDisplay(value); return; }

    const dur = 2000; // Premium slow-decelerating count
    let animationFrameId;
    
    const timeoutId = setTimeout(() => {
      const startTs = performance.now();
      const tick = (ts) => {
        const elapsed = ts - startTs;
        const progress = Math.min(1, elapsed / dur);
        
        // easeOutQuart: extremely smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 4);
        const current = Math.round(parsed.num * eased);
        
        setDisplay(`${parsed.prefix}${current}${parsed.suffixK}${parsed.suffixPlus}`);
        
        if (progress < 1) {
          animationFrameId = requestAnimationFrame(tick);
        } else {
          setDisplay(value);
        }
      };
      animationFrameId = requestAnimationFrame(tick);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, value, delay]);

  return <span ref={ref}>{display}</span>;
};

const Numbers = () => {
  const { isAr, t } = useLang();
  
  return (
    <section
      id="numbers"
      className="relative bg-brand-green text-bg-ivory py-24 md:py-36 overflow-hidden border-b border-brand-green-dark"
      data-testid="numbers-section"
    >
      {/* Blueprint Grid Mesh Background */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(232, 211, 195, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(232, 211, 195, 0.15) 1px, transparent 1px)`, 
          backgroundSize: "64px 64px" 
        }} 
      />

      {/* Decorative Blueprint Corner Marks */}
      <div className="absolute top-8 left-8 w-4 h-4 border-t border-l border-brand-champagne/15 pointer-events-none" />
      <div className="absolute top-8 right-8 w-4 h-4 border-t border-r border-brand-champagne/15 pointer-events-none" />
      <div className="absolute bottom-8 left-8 w-4 h-4 border-b border-l border-brand-champagne/15 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-4 h-4 border-b border-r border-brand-champagne/15 pointer-events-none" />

      {/* Radial glow / Orbit Pattern */}
      <div className="absolute -bottom-[260px] -end-[200px] pointer-events-none select-none opacity-20">
        <OrbitPattern size={900} opacity={1} color="#E8D3C3" />
      </div>

      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Top Mini Meta Header */}
        <div 
          className="flex justify-between items-center text-brand-champagne/40 mb-16 border-b border-brand-champagne/10 pb-6"
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-widest uppercase text-brand-champagne/60">
              [ {t("القدرة والإنتاج", "CAPACITY & STATISTICS")} ]
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs tracking-widest hidden md:inline">BB // STATE-OF-THE-ART</span>
            <span className="font-mono text-xs">04 — SEC</span>
          </div>
        </div>

        {/* Section Intro Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease }}
          className={`mb-24 max-w-4xl ${isAr ? "text-right" : "text-left"}`}
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-champagne/70 mb-5 tracking-widester`}>
            {t("✦ القدرة بالأرقام", "✦ INDUSTRIAL SCALE IN NUMBERS")}
          </div>
          <h2
            className={`${isAr ? "font-arabic font-semibold" : "font-serif"} text-bg-ivory leading-[1.08] tracking-tight`}
            style={{ fontSize: isAr ? "clamp(38px, 5vw, 70px)" : "clamp(48px, 5.6vw, 92px)" }}
          >
            {isAr ? (
              <>
                أرقام تروي <br />
                <span className="font-light italic text-brand-champagne/90">قدرتنا الصناعية ونطاق عملياتنا</span>
              </>
            ) : (
              <>
                Numbers Behind <br />
                <span className="font-light italic text-brand-champagne/80">Our Industrial Operations</span>
              </>
            )}
          </h2>
        </motion.div>

        {/* Dynamic Bento-Styled Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-brand-champagne/10"
          dir={isAr ? "rtl" : "ltr"}
        >
          {numbers.map((n, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.3, ease, delay: i * 0.08 }}
              className="group relative p-8 md:p-12 border-r border-b border-brand-champagne/10 hover:bg-white/[0.015] transition-colors duration-500 flex flex-col justify-between min-h-[260px]"
            >
              {/* Sleek expanding active underline indicator */}
              <div className={`absolute bottom-0 ${isAr ? "right-0" : "left-0"} h-[2px] w-0 bg-brand-champagne transition-all duration-500 group-hover:w-full`} />

              {/* Number Container */}
              <div 
                className="font-serif-display text-brand-champagne transition-transform duration-700 ease-out group-hover:-translate-y-1 group-hover:scale-[1.02]"
                style={{ fontSize: "clamp(64px, 7.5vw, 110px)", lineHeight: 0.85 }}
              >
                <Counter value={n.value} delay={i * 120} />
              </div>

              {/* Subtext info */}
              <div className="mt-8">
                <div
                  className={`${isAr ? "font-arabic font-semibold" : "font-sans font-medium"} text-bg-ivory text-[17px] md:text-[18px] group-hover:text-brand-champagne transition-colors duration-300`}
                >
                  {isAr ? n.ar_label : n.en_label}
                </div>
                
                <div
                  className={`${isAr ? "font-arabic" : "font-sans"} text-bg-ivory/55 mt-2.5 text-[13px] leading-relaxed font-light`}
                >
                  {isAr ? n.ar_sub : n.en_sub}
                </div>
              </div>

              {/* Top corner cell coordinate label */}
              <span className="absolute top-4 right-4 md:top-6 md:right-6 font-mono text-[9px] text-brand-champagne/20 group-hover:text-brand-champagne/40 transition-colors">
                [ 0{i + 1} // CELL ]
              </span>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Numbers;
